import type { WorkspaceDb } from './workspace-tables'
import { s } from '@w/schema'
import { safe } from '@w/utils'

/** Search carries the merged content version and scalar timestamp it indexed; no dirty queue. */
export const PAGE_SEARCH_REFRESH = {
  work: `SELECT p.id AS id FROM pages p LEFT JOIN pageSearch s ON s.id = p.id
    WHERE s.id IS NULL OR crdt_version(p.data) IS NOT s.searchVersion
      OR p.updatedAt IS NOT s.searchUpdatedAt OR p.deletedAt IS NOT s.deletedAt
    UNION ALL SELECT s.id AS id FROM pageSearch s LEFT JOIN pages p ON p.id = s.id WHERE p.id IS NULL`,
  remove: `DELETE FROM pageSearch WHERE id IN (SELECT value FROM json_each(?1)) AND NOT EXISTS (SELECT 1 FROM pages p WHERE p.id = pageSearch.id)`,
  // Cast json_each ids so each lookup probes the page primary key.
  upsert: `INSERT INTO pageSearch(id, title, text, deletedAt, searchVersion, searchUpdatedAt)
    SELECT p.id, p.title, crdt_text(p.data), p.deletedAt, crdt_version(p.data), p.updatedAt
    FROM json_each(?1) w JOIN pages p ON p.id = CAST(w.value AS TEXT) WHERE true
    ON CONFLICT(id) DO UPDATE SET title = excluded.title, text = excluded.text, deletedAt = excluded.deletedAt,
      searchVersion = excluded.searchVersion, searchUpdatedAt = excluded.searchUpdatedAt`,
} as const

type RefreshQuery = (sql: string, params?: unknown[]) => Promise<unknown[]>
const workSchema = s.array(s.object({ id: s.string() }))

/** One refresh over a raw query function (the app's transaction driver, or the bench's connection). Returns the pages
 * it indexed. `between` runs after the work set is read, before any write (tests interleave a replay there). */
export async function runPageSearchRefresh(query: RefreshQuery, between?: (query: RefreshQuery) => Promise<void>): Promise<number> {
  const work = workSchema.parse(await query(PAGE_SEARCH_REFRESH.work))
  if (work.length === 0) return 0
  await between?.(query)
  const ids = JSON.stringify(work.map(row => row.id))
  await query(PAGE_SEARCH_REFRESH.remove, [ids])
  await query(PAGE_SEARCH_REFRESH.upsert, [ids])
  return work.length
}

/** Text and its indexed version commit together; failure leaves the previous version stale. */
export async function refreshPageSearch(db: WorkspaceDb, between?: (query: RefreshQuery) => Promise<void>): Promise<void> {
  const work = await safe(db.driver).query(PAGE_SEARCH_REFRESH.work)
  if (work.length === 0) return
  await db.transaction(async tx => {
    const driver = safe(tx.driver)
    await runPageSearchRefresh((statement, params) => driver.query(statement, params), between)
  })
}

/** Refresh at startup and on pages invalidation; coalesce invalidations during a drain. */
export function startPageSearchRefresh(db: WorkspaceDb, onError: (error: unknown) => void) {
  const driver = db.driver as (WorkspaceDb['driver'] & { subscribe?(sql: string, onInvalidate: () => void): Promise<() => void> }) | undefined
  let again = false, closed = false
  let running: Promise<void> | undefined
  let closing: Promise<void> | undefined
  const drain = () => {
    if (closed) return
    if (running) { again = true; return }
    running = refreshPageSearch(db).catch(onError).finally(() => {
      running = undefined
      if (again) { again = false; drain() }
    })
  }
  const release = driver?.subscribe?.('SELECT id, crdt_version(data) AS contentVersion, updatedAt, deletedAt FROM pages', drain)
  drain()
  const ready = running ?? Promise.resolve()
  return {
    /** The startup drain: pages pulled before open are searchable once it settles. */
    ready,
    /** Idempotent: retirement and the later workspace close both stop the drain. */
    close() {
      closing ??= (async () => {
        closed = true
        const stop = await release
        stop?.()
        await running
      })()
      return closing
    },
  }
}

